/** Dev21 — Family Tree / Relations. Two-sided hierarchy with connector lines. */

import React from 'react';
import { motion } from 'motion/react';
import { Users } from 'lucide-react';
import { Section, SectionHeading, Reveal } from '../components/primitives';
import { DEV21_EASE } from '../theme';
import type { Dev21Content, FamilyGroup } from '../content';

function FamilyColumn({
  group,
  delay,
}: {
  group: FamilyGroup;
  delay: number;
  key?: React.Key;
}) {
  return (
    <Reveal delay={delay} className="flex flex-col items-center">
      {/* Group crown */}
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--d21-accent)]/12 text-[var(--d21-accent)]">
          <Users size={22} />
        </span>
        <h3 className="font-serif text-2xl font-semibold text-[var(--d21-ink)]">
          {group.title}
        </h3>
        <span className="text-[0.66rem] font-semibold uppercase tracking-[0.24em] text-[var(--d21-accent)]">
          {group.side === 'groom' ? "Groom's Side" : "Bride's Side"}
        </span>
      </div>

      {/* Vertical connector */}
      <span className="my-5 h-8 w-px bg-[var(--d21-border)]" />

      {/* Member cards */}
      <div className="flex w-full flex-col gap-3">
        {group.members.map((member, i) => (
          <motion.div
            key={`${member.name}-${i}`}
            initial={{ opacity: 0, x: group.side === 'groom' ? -24 : 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: DEV21_EASE }}
            className="rounded-2xl border border-[var(--d21-border)] bg-[var(--d21-surface)] px-5 py-4 text-center shadow-sm"
          >
            <p className="font-medium text-[var(--d21-ink)]">{member.name}</p>
            <p className="mt-0.5 text-sm text-[var(--d21-muted)]">
              {member.relation}
            </p>
          </motion.div>
        ))}
      </div>
    </Reveal>
  );
}

export function FamilySection({
  content,
}: {
  content: Dev21Content['family'];
}) {
  return (
    <Section id="family" tone="cream">
      <SectionHeading label={content.label} title={content.title} />

      <div className="relative mt-14 grid gap-12 md:grid-cols-2 md:gap-8">
        {content.groups.map((group, i) => (
          <FamilyColumn key={group.title} group={group} delay={i * 0.12} />
        ))}

        {/* Joining node between the two families */}
        <div className="pointer-events-none absolute left-1/2 top-9 hidden -translate-x-1/2 md:block">
          <motion.span
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: DEV21_EASE }}
            className="block h-3 w-3 rounded-full bg-[var(--d21-accent)] ring-4 ring-[var(--d21-accent)]/20"
          />
        </div>
      </div>
    </Section>
  );
}
